import './styles/main.css';
import { AuthUser } from './types';
import { AuthService } from './services/auth';
import { AuthComponent } from './components/auth';
import { DashboardComponent } from './components/dashboard';

class GemQuestPartnerPortal {
  private authService: AuthService;
  private currentUser: AuthUser | null = null;
  private authComponent: AuthComponent | null = null;
  private dashboardComponent: DashboardComponent | null = null;

  constructor() {
    this.authService = new AuthService();
    // Removed automatic refresh listeners to prevent refresh loops
    // Manual refresh is still available via the refresh button
  }

  async init(): Promise<void> {
    try {
      // Check for existing authentication
      this.currentUser = await this.authService.getCurrentUser();
      
      // Check if user is returning from Stripe payment
      const urlParams = new URLSearchParams(window.location.search);
      const isReturningFromPayment = urlParams.has('payment');
      
      if (this.currentUser) {
        await this.showDashboard();
        
        // If returning from payment, trigger data refresh
        if (isReturningFromPayment) {
          this.handlePaymentReturn(urlParams);
        }
      } else {
        this.showAuth();
      }

      // Listen for auth state changes
      this.authService.onAuthStateChange((user) => {
        this.currentUser = user;
        if (user) {
          this.showDashboard();
        } else {
          this.showAuth();
        }
      });

    } catch (error) {
      console.error('Initialization failed:', error);
      this.showAuth();
    }
  }

  private showAuth(): void {
    const authContainer = document.getElementById('auth-container')!;
    const dashboardContainer = document.getElementById('dashboard-container')!;
    
    authContainer.style.display = 'block';
    dashboardContainer.style.display = 'none';
    
    this.authComponent = new AuthComponent(this.authService, authContainer);
    this.authComponent.render();
  }

  private async showDashboard(): Promise<void> {
    if (!this.currentUser) return;
    
    const authContainer = document.getElementById('auth-container')!;
    const dashboardContainer = document.getElementById('dashboard-container')!;
    
    authContainer.style.display = 'none';
    dashboardContainer.style.display = 'block';
    
    this.dashboardComponent = new DashboardComponent(
      this.authService, 
      dashboardContainer, 
      this.currentUser
    );
    this.dashboardComponent.render();
  }

  private handlePaymentReturn(urlParams: URLSearchParams): void {
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      console.log('User returned from successful payment, refreshing data...');
      
      // Clear URL parameters to clean up the URL
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      window.history.replaceState({}, '', url.toString());
      
      // Wait a moment for webhooks to process, then refresh data
      setTimeout(async () => {
        if (this.dashboardComponent) {
          await this.dashboardComponent.refreshAllData();
        }
      }, 2000); // Wait 2 seconds for webhook processing
      
      // Also refresh immediately in case webhook already processed
      if (this.dashboardComponent) {
        this.dashboardComponent.refreshAllData();
      }
    } else if (paymentStatus === 'cancelled') {
      console.log('User cancelled payment');
      
      // Clear URL parameters
      const url = new URL(window.location.href);
      url.searchParams.delete('payment');
      window.history.replaceState({}, '', url.toString());
    }
  }
}

// Initialize the application
document.addEventListener('DOMContentLoaded', () => {
  const app = new GemQuestPartnerPortal();
  app.init().catch(error => {
    console.error('Failed to initialize application:', error);
  });
});
